import { useEffect, useState, type ChangeEvent } from 'react';
import { Link2, Plus } from 'lucide-react';
import { Button, EmptyState } from '@/components/ui';
import type {
  AutoMatchResult,
  MatchingMode,
  PointPair,
  SystemPoint,
  TransformationSolution,
} from '@/types';
import { autoMatchPoints } from '@/utils/pointMatching';
import { MatchingReview } from './MatchingReview';
import { MatchingStrategies } from './MatchingStrategies';
import { PointTable } from './PointTable';

interface PointPairsTabProps {
  pointPairs: PointPair[];
  system1Points: SystemPoint[];
  system2Points: SystemPoint[];
  matchingMode: MatchingMode;
  detectedOffset: number | null;
  solution: TransformationSolution | null;
  solutionStale: boolean;
  trialSolution?: TransformationSolution | null;
  selectedPointId: number | null;
  flaggedPointIds: readonly number[];
  onSelectPoint: (id: number) => void;
  onEditPair: (id: number) => void;
  onAddPair: () => void;
  onSetWeight: (id: number, weight: number) => void;
  onResetWeights: () => void;
  onImportPairs: (event: ChangeEvent<HTMLInputElement>) => void;
  onAcceptPairs: (
    pairs: PointPair[],
    mode: MatchingMode,
    detectedOffset?: number
  ) => void;
  onMatchingModeChange: (mode: MatchingMode) => void;
}

/**
 * Correspondence review for Adjust: matching strategies, proposal review,
 * and the shared point table.
 */
export const PointPairsTab = ({
  pointPairs,
  system1Points,
  system2Points,
  matchingMode,
  detectedOffset,
  solution,
  solutionStale,
  trialSolution,
  selectedPointId,
  flaggedPointIds,
  onSelectPoint,
  onEditPair,
  onAddPair,
  onSetWeight,
  onResetWeights,
  onImportPairs,
  onAcceptPairs,
  onMatchingModeChange,
}: PointPairsTabProps) => {
  const hasData = system1Points.length > 0 && system2Points.length > 0;
  const [proposal, setProposal] = useState<AutoMatchResult | null>(null);
  const [strategiesOpen, setStrategiesOpen] = useState(pointPairs.length === 0);

  // With no pairs yet, the first suggestion is the main content.
  useEffect(() => {
    setProposal(
      system1Points.length && system2Points.length && !pointPairs.length
        ? autoMatchPoints(system1Points, system2Points, 'auto')
        : null
    );
  }, [system1Points, system2Points, pointPairs]);
  useEffect(() => {
    if (pointPairs.length) setStrategiesOpen(false);
  }, [pointPairs.length]);

  const propose = (mode: MatchingMode) =>
    setProposal(autoMatchPoints(system1Points, system2Points, mode));
  const recordUsed = new Set(pointPairs.map((pair) => pair.sys1Point));
  const fieldUsed = new Set(pointPairs.map((pair) => pair.sys2Point));
  const inlineReview = proposal !== null && pointPairs.length === 0;

  return (
    <div className="space-y-4">
      {hasData && (
        <MatchingStrategies
          open={strategiesOpen}
          onOpenChange={setStrategiesOpen}
          matchingMode={matchingMode}
          detectedOffset={detectedOffset}
          pairCount={pointPairs.length}
          unmatchedRecord={
            system1Points.filter((point) => !recordUsed.has(point.point)).length
          }
          unmatchedField={
            system2Points.filter((point) => !fieldUsed.has(point.point)).length
          }
          onMatchingModeChange={onMatchingModeChange}
          onPreview={() => propose(matchingMode)}
          onSuggest={() => propose('auto')}
        />
      )}
      {proposal && (
        <MatchingReview
          pairs={proposal.pairs}
          system1Points={system1Points}
          system2Points={system2Points}
          method={proposal.mode}
          replacedCount={pointPairs.length}
          presentation={inlineReview ? 'inline' : 'dialog'}
          onAccept={(reviewedPairs) => {
            onAcceptPairs(
              reviewedPairs,
              proposal.mode,
              proposal.detectedOffset
            );
            setProposal(null);
            setStrategiesOpen(false);
          }}
          onCancel={() => setProposal(null)}
        />
      )}
      {!inlineReview && (
        <PointTable
          pointPairs={pointPairs}
          solution={solution}
          solutionStale={solutionStale}
          trialSolution={trialSolution}
          selectedPointId={selectedPointId}
          flaggedPointIds={flaggedPointIds}
          canImportPairs={hasData}
          onSelectPoint={onSelectPoint}
          onEditPair={onEditPair}
          onSetWeight={onSetWeight}
          onAddPair={onAddPair}
          onResetWeights={onResetWeights}
          onImportPairs={onImportPairs}
          emptyState={
            <EmptyState
              icon={<Link2 />}
              title="No pairs yet"
              description={
                hasData
                  ? 'Preview matches from the strategies above, add a pair by hand, or link points on the map.'
                  : 'Import Record and Field coordinates to match them, or add a pair by hand.'
              }
              actions={
                <Button leadingIcon={<Plus />} onClick={onAddPair}>
                  Add pair
                </Button>
              }
            />
          }
        />
      )}
    </div>
  );
};
